import { useCallback, useEffect, useRef, useState } from 'react';

type NotificationShow = (message: string, severity: string, autoclose: boolean, format: string) => void;
type GridMapType = 'cadastral' | 'osm';

type UseCadastralLayerRecoveryOptions = {
  mapType: GridMapType;
  setMapType: (nextMapType: GridMapType) => void;
  showNotification: NotificationShow;
  loadTimeoutMs?: number;
  maxRetries?: number;
};

type StableHandler<TArgs extends unknown[]> = (...args: TArgs) => void;

const DEFAULT_LOAD_TIMEOUT_MS = 10000;
const DEFAULT_MAX_RETRIES = 2;
const FALLBACK_TOAST_MESSAGE =
  'The cadastral map is temporarily unavailable. We switched to Open Street Map. Please try again later.';

const useStableHandler = <TArgs extends unknown[]>(handler: StableHandler<TArgs>) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  return useCallback((...args: TArgs) => handlerRef.current?.(...args), []);
};

export const useCadastralLayerRecovery = ({
  mapType,
  setMapType,
  showNotification,
  loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
  maxRetries = DEFAULT_MAX_RETRIES,
}: UseCadastralLayerRecoveryOptions) => {
  const [cadastralLayerKey, setCadastralLayerKey] = useState(0);
  const retryCountRef = useRef(0);
  const attemptRef = useRef({ pending: 0, hasLoaded: false });
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousMapTypeRef = useRef(mapType);

  const clearFallbackTimeout = useCallback(() => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  }, []);

  const resetAttempt = useCallback(() => {
    attemptRef.current = { pending: 0, hasLoaded: false };
  }, []);

  const handleFailure = useCallback(() => {
    if (mapType !== 'cadastral' || attemptRef.current.hasLoaded) {
      return;
    }

    if (retryCountRef.current < maxRetries) {
      retryCountRef.current += 1;
      resetAttempt();
      setCadastralLayerKey((prev) => prev + 1);
      return;
    }

    setMapType('osm');
    showNotification(FALLBACK_TOAST_MESSAGE, 'warning', true, 'toast');
  }, [mapType, maxRetries, setMapType, showNotification]);

  useEffect(() => {
    const switchedToCadastral = previousMapTypeRef.current !== 'cadastral' && mapType === 'cadastral';
    previousMapTypeRef.current = mapType;

    clearFallbackTimeout();

    if (mapType !== 'cadastral') {
      resetAttempt();
      return undefined;
    }

    if (switchedToCadastral) {
      retryCountRef.current = 0;
    }

    resetAttempt();
    fallbackTimeoutRef.current = setTimeout(() => {
      handleFailure();
    }, loadTimeoutMs);

    return () => {
      clearFallbackTimeout();
    };
  }, [cadastralLayerKey, clearFallbackTimeout, handleFailure, loadTimeoutMs, mapType, resetAttempt]);

  const onCadastralTileLoadStart = useStableHandler(() => {
    if (mapType !== 'cadastral') {
      return;
    }

    attemptRef.current.pending += 1;
  });

  const onCadastralTileLoad = useStableHandler(() => {
    attemptRef.current.pending = Math.max(0, attemptRef.current.pending - 1);
    if (attemptRef.current.pending === 0) {
      attemptRef.current.hasLoaded = true;
      retryCountRef.current = 0;
      clearFallbackTimeout();
    }
  });

  const onCadastralTileError = useStableHandler(() => {
    if (mapType !== 'cadastral') {
      return;
    }

    attemptRef.current.pending = Math.max(0, attemptRef.current.pending - 1);

    if (attemptRef.current.pending === 0) {
      handleFailure();
    }
  });

  return {
    cadastralLayerKey,
    onCadastralTileLoadStart,
    onCadastralTileLoad,
    onCadastralTileError,
  };
};
